// People's dialogs, on FormDialog (BL-189): choosing someone's roles, where a role applies, turning a
// sign-in off or back on (or an owner's yes to an invitation as owner), and inviting someone. The server
// asks first when it must: a fresh code from the owner's authenticator app (QA-685), then the change; and
// an "I understand" before a technician gets owner-only power on the iPad. Each dialog keeps what was
// typed and says why it didn't save. Blueprint's PeoplePage.tsx and Riverstone's People (Settings.tsx)
// both open them.
import { useState } from 'react';
import { RpcError } from '@flowology/client';
import { Checkbox, Choice, ChoiceGroup, FormDialog, Notice, TextField } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import { stepUpWith } from '../shared/stepUp.ts';
import { useStepUp } from '../shared/StepUpAsk.tsx';
import { invitedWords, type SignInState } from './settings-model.ts';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';
/** What the server asked before it would make the change: a fresh code, or an "I understand". */
const askedFor = (e: unknown): 'step_up' | 'field' | null => {
  const reason = e instanceof RpcError ? (e.detail as { reason?: string } | undefined)?.reason : undefined;
  return reason === 'step_up_required' ? 'step_up' : reason === 'field_role_confirm' ? 'field' : null;
};
const ASKED = {
  step_up: 'Confirm it’s you first: a code from your authenticator app.',
  field: 'Say you understand first.',
};

export interface Person {
  staffId: string;
  displayName: string;
  email: string | null;
  isTechnician: boolean;
  status: string;
  roleKeys: string[] | null;
  scopes: { roleKey: string; businessUnitIds: string[]; locationIds: string[] }[];
  grants: { permission: string; expiresAt: string | null }[] | null;
  /** The email they sign in with; null when they don't. */
  signInEmail: string | null;
  signIn: SignInState | null;
}
export interface Unit {
  id: string;
  name: string;
}
/** Off, back on, or an owner's yes to an invitation as owner (QA-355). */
export type SwitchMode = 'off' | 'on' | 'confirm';

/** The code box, when the server asked for a fresh second factor. */
function CodeField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <TextField
      label="Confirm it’s you: a code from your authenticator app"
      inputMode="numeric"
      autoComplete="one-time-code"
      value={value}
      onChange={onChange}
    />
  );
}

/** A technician given owner-only power: what it means, and the "I understand" it needs. */
function FieldWarning({
  who,
  words,
  understand,
  understood,
  onChange,
}: {
  who: string;
  /** What it would mean, after "<who> works in the field." */
  words: string;
  /** The tick's words, "I understand …". */
  understand: string;
  understood: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <>
      <Notice title={`${who} works in the field.`}>{words}</Notice>
      <Checkbox size="touch-on-phone" isSelected={understood} onChange={onChange}>
        {understand}
      </Checkbox>
    </>
  );
}

/**
 * Choosing someone's roles (people.setRoles, the owner's). It asks for a fresh two-factor code when the
 * server does, and for an "I understand" when a technician would get owner-only power.
 */
export function RolesDialog({
  person,
  start,
  asked,
  roleNames,
  onClose,
  onDone,
}: {
  person: Person;
  /** The roles asked for (a role taken away, a role added), when the server asked first. */
  start?: string[];
  asked?: 'step_up' | 'field';
  roleNames: Record<string, string>;
  onClose: () => void;
  onDone: (said: string) => Promise<void>;
}) {
  const [keys, setKeys] = useState<string[]>(start ?? person.roleKeys ?? []);
  const [ask, setAsk] = useState<'step_up' | 'field' | null>(asked ?? null);
  const [code, setCode] = useState('');
  const [understood, setUnderstood] = useState(false);
  const save = async () => {
    if (ask === 'step_up') {
      const up = await stepUpWith(code);
      if (!up.ok) throw new Error(up.message);
    }
    try {
      await client.call('people.setRoles', {
        staffId: person.staffId,
        roleKeys: keys,
        ...(ask === 'field' && understood ? { confirm: true } : {}),
      });
    } catch (e) {
      const more = askedFor(e);
      if (more) {
        setAsk(more);
        throw new Error(ASKED[more], { cause: e });
      }
      throw new Error(message(e), { cause: e });
    }
    await onDone(`${person.displayName}’s roles are changed.`);
  };
  return (
    <FormDialog
      isOpen
      title={`${person.displayName}’s roles`}
      submitLabel={ask === 'field' ? 'Change anyway' : 'Save roles'}
      isDirty={false}
      isSubmitDisabled={
        !keys.length || (ask === 'field' && !understood) || (ask === 'step_up' && !code.trim())
      }
      onClose={onClose}
      onSubmit={save}
    >
      <ChoiceGroup
        label="Roles"
        description="They sign in with these, and may do what any of them allows. The company always keeps an owner."
        selectionMode="multiple"
        value={keys}
        onChange={setKeys}
      >
        {Object.entries(roleNames).map(([k, name]) => (
          <Choice key={k} value={k}>
            {name}
          </Choice>
        ))}
      </ChoiceGroup>
      {ask === 'step_up' && <CodeField value={code} onChange={setCode} />}
      {ask === 'field' && (
        <FieldWarning
          who={person.displayName}
          words={`This would put owner-only permissions on ${person.displayName}’s iPad, out of the office.`}
          understand={`I understand ${person.displayName} will have owner-only permissions on the iPad, out of the office.`}
          understood={understood}
          onChange={setUnderstood}
        />
      )}
    </FormDialog>
  );
}

/** Where one of someone's roles applies (roles.setScope, the owner's): some departments, or all of them. */
export function LimitDialog({
  person,
  roleKey,
  roleName,
  units,
  onClose,
  onDone,
}: {
  person: Person;
  roleKey: string;
  roleName: string;
  units: Unit[];
  onClose: () => void;
  onDone: (said: string) => Promise<void>;
}) {
  const current = person.scopes.find((s) => s.roleKey === roleKey)?.businessUnitIds ?? [];
  const [picked, setPicked] = useState<string[]>(current);
  // a role's limits ask the owner's fresh second factor (QA-685)
  const { run, ask } = useStepUp();
  const save = async () => {
    await run(() =>
      client.call('roles.setScope', {
        staffId: person.staffId,
        roleKey,
        businessUnitIds: picked,
        locationIds: person.scopes.find((s) => s.roleKey === roleKey)?.locationIds ?? [],
      }),
    ).catch((err: unknown) => {
      throw new Error(message(err));
    });
    await onDone(
      picked.length
        ? `${person.displayName}’s ${roleName} role now covers only ${picked.length} department${picked.length === 1 ? '' : 's'}.`
        : `${person.displayName}’s ${roleName} role covers the whole company again.`,
    );
  };
  return (
    <>
      <FormDialog
        isOpen
        title={`Where ${person.displayName}’s ${roleName} role applies`}
        submitLabel="Save"
        isDirty={false}
        onClose={onClose}
        onSubmit={save}
        // What the limit really does (docs/specs/scoped-roles.md §3): job-bound records follow it;
        // company-level records stay open; actions that reach every place are refused.
        note={
          picked.length ? (
            <>
              <b>
                {roleName} for {person.displayName} reaches only{' '}
                {units
                  .filter((u) => picked.includes(u.id))
                  .map((u) => u.name)
                  .join(' and ')}
                :
              </b>{' '}
              their jobs, visits and invoices. Customers, the pricebook, staff and settings stay company-wide;
              anything that reaches every place (a new department, company-wide settings, the Books’ aging and
              statements) is refused. What another of their roles allows stays as it is.
            </>
          ) : (
            <>
              <b>Company-wide:</b> the {roleName} role reaches every department.
            </>
          )
        }
      >
        <ChoiceGroup
          label="Departments"
          description="Tick the departments it covers. Untick everything to make it company-wide."
          selectionMode="multiple"
          value={picked}
          onChange={setPicked}
        >
          {units.map((u) => (
            <Choice key={u.id} value={u.id}>
              {u.name}
            </Choice>
          ))}
        </ChoiceGroup>
      </FormDialog>
      {ask}
    </>
  );
}

/**
 * Turning someone's sign-in off, with the reason (people.disable), or back on (people.enable), or an
 * owner's yes to an invitation as owner (people.confirmOwnerInvite): the owner's, with a fresh two-factor
 * code when the server asks.
 */
export function SignInSwitchDialog({
  person,
  mode,
  onClose,
  onDone,
}: {
  /** Who: a person on the list, or an invitation with no staff record (by its membership). */
  person: { displayName: string; staffId?: string; membershipId?: string };
  mode: SwitchMode;
  onClose: () => void;
  onDone: (said: string) => Promise<void>;
}) {
  const off = mode === 'off';
  const [why, setWhy] = useState('');
  const [ask, setAsk] = useState(false);
  const [code, setCode] = useState('');
  const save = async () => {
    if (ask) {
      const up = await stepUpWith(code);
      if (!up.ok) throw new Error(up.message);
    }
    try {
      if (off) await client.call('people.disable', { staffId: person.staffId, reason: why.trim() });
      else if (mode === 'on') await client.call('people.enable', { staffId: person.staffId });
      else
        await client.call(
          'people.confirmOwnerInvite',
          person.staffId ? { staffId: person.staffId } : { membershipId: person.membershipId },
        );
    } catch (e) {
      if (askedFor(e) === 'step_up') {
        setAsk(true);
        throw new Error(ASKED.step_up, { cause: e });
      }
      throw new Error(message(e), { cause: e });
    }
    await onDone(
      off
        ? `${person.displayName}’s sign-in is off. Their iPad signs out the next time it connects.`
        : mode === 'on'
          ? `${person.displayName} can sign in again.`
          : `${person.displayName} can sign in as an owner now.`,
    );
  };
  return (
    <FormDialog
      isOpen
      title={
        off
          ? `Turn off ${person.displayName}’s sign-in`
          : mode === 'on'
            ? `Turn ${person.displayName}’s sign-in back on`
            : `Confirm ${person.displayName} as an owner`
      }
      submitLabel={off ? 'Turn off sign-in' : mode === 'on' ? 'Turn sign-in back on' : 'Confirm as owner'}
      isDirty={false}
      isSubmitDisabled={(off && !why.trim()) || (ask && !code.trim())}
      onClose={onClose}
      onSubmit={save}
      note={
        off
          ? `${person.displayName} is signed out everywhere now. Their iPad signs out the next time it connects, and what it hadn’t sent comes to you to review. Their work and history stay.`
          : mode === 'on'
            ? `${person.displayName} signs in again, as before. Nothing they were signed in to comes back.`
            : `${person.displayName} was invited as an owner without an owner’s yes. Once you confirm, they can sign in and do everything an owner does. Not expected? Cancel the invitation instead.`
      }
    >
      {off && <TextField label="Why" maxLength={300} value={why} onChange={setWhy} />}
      {ask && <CodeField value={code} onChange={setCode} />}
    </FormDialog>
  );
}

/**
 * Inviting someone to sign in (people.invite, QA-322): their email, name and roles. A fresh two-factor
 * code when the server asks (an owner invited), an "I understand" for a technician made owner; then what
 * was sent, or what to tell them when no email goes, said on the page once the dialog closes.
 */
export function InviteDialog({
  owner,
  roleNames,
  onClose,
  onInvited,
}: {
  owner: boolean;
  roleNames: Record<string, string>;
  onClose: () => void;
  onInvited: (said: string) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [keys, setKeys] = useState<string[]>([]);
  const [ask, setAsk] = useState<'step_up' | 'field' | null>(null);
  const [code, setCode] = useState('');
  const [understood, setUnderstood] = useState(false);
  // Only the owner invites an owner; the server says so too.
  const offered = Object.entries(roleNames).filter(([k]) => owner || k !== 'owner');
  const save = async () => {
    if (ask === 'step_up') {
      const up = await stepUpWith(code);
      if (!up.ok) throw new Error(up.message);
    }
    let r: { emailed: boolean; signInAt: string | null };
    try {
      r = await client.call<{ emailed: boolean; signInAt: string | null }>('people.invite', {
        email: email.trim(),
        displayName: name.trim(),
        roleKeys: keys,
        ...(ask === 'field' && understood ? { confirm: true } : {}),
      });
    } catch (e) {
      const more = askedFor(e);
      if (more) {
        setAsk(more);
        throw new Error(ASKED[more], { cause: e });
      }
      throw new Error(message(e), { cause: e });
    }
    await onInvited(invitedWords(r, name.trim(), email.trim()));
  };
  return (
    <FormDialog
      isOpen
      title="Invite someone to sign in"
      submitLabel={ask === 'field' ? 'Invite anyway' : 'Invite'}
      isDirty={!!(email.trim() || name.trim() || keys.length)}
      isSubmitDisabled={
        !keys.length ||
        !email.trim() ||
        !name.trim() ||
        (ask === 'field' && !understood) ||
        (ask === 'step_up' && !code.trim())
      }
      onClose={onClose}
      onSubmit={save}
    >
      <TextField
        label="Their email"
        description="They sign in with it: Google or Microsoft with this address, or a code sent to it. The invitation lasts 14 days."
        type="email"
        autoComplete="off"
        value={email}
        onChange={setEmail}
      />
      <TextField label="Their name" value={name} onChange={setName} />
      <ChoiceGroup
        label="Roles"
        description="They sign in with these roles."
        selectionMode="multiple"
        value={keys}
        onChange={setKeys}
      >
        {offered.map(([k, label]) => (
          <Choice key={k} value={k}>
            {label}
          </Choice>
        ))}
      </ChoiceGroup>
      {ask === 'step_up' && <CodeField value={code} onChange={setCode} />}
      {ask === 'field' && (
        <FieldWarning
          who={name.trim() || 'They'}
          words="As owner, they would have owner-only permissions on their iPad, out of the office."
          understand="I understand they will have owner-only permissions on the iPad, out of the office."
          understood={understood}
          onChange={setUnderstood}
        />
      )}
    </FormDialog>
  );
}
